import React, { useEffect, useState } from 'react'
import { Button, Flex, Input, Spin, message } from 'antd'
import { format } from 'date-fns'
import AuthSelectors from 'modules/domain/auth/selectors'
import { addNote, getNotesList, updateNote } from 'modules/domain/note/managers'
import type { Note } from 'modules/domain/note/types'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { NoteType } from 'types/entities'
import type { SeedOrder } from '../SeedOrdersList/SeedOrdersList'
import STNoteDropdownActions from 'views/components/SeedTracker/STNoteDropdownActions'
import {
  seedOrderNotesEditActionsStyle,
  seedOrderNotesEmptyStyle,
  seedOrderNotesItemStyle,
  seedOrderNotesLoadingStyle,
  seedOrderNotesMarkerStyle,
  seedOrderNotesMetaStyle,
  seedOrderNotesMoreActionsWrapStyle,
  seedOrderNotesRailStyle,
  seedOrderNotesRootStyle,
  seedOrderNotesTextStyle,
  seedOrderNotesTimelineStyle,
} from '../styles'

type SeedOrderNotesProps = {
  order: SeedOrder | null
  isAddingNote: boolean
  onCancelAddNote: () => void
  onSaveAddNote: () => void
}

function formatNoteMeta(createdAt: string | undefined, createdBy: string | undefined): string {
  if (!createdAt && !createdBy) return ''
  if (!createdAt) return createdBy ?? ''
  const date = new Date(createdAt)
  const datePart = Number.isNaN(date.getTime()) ? createdAt : format(date, 'dd MMMM yyyy h:mm a')
  return createdBy ? `${datePart} - ${createdBy}` : datePart
}

const SeedOrderNotes: React.FC<SeedOrderNotesProps> = ({ order, isAddingNote, onCancelAddNote, onSaveAddNote }) => {
  const { t } = useTranslation(['notes'])
  const profile = useSelector(AuthSelectors.profile)
  const [noteText, setNoteText] = useState('')
  const [notes, setNotes] = useState<Note[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [editSaving, setEditSaving] = useState(false)
  const currentUserFullName = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()
  const hideMoreOptions = isAddingNote || editingNoteId !== null

  const isCurrentUserNote = (createdBy?: string) =>
    !!currentUserFullName && (createdBy ?? '').trim().toLowerCase() === currentUserFullName.toLowerCase()

  useEffect(() => {
    if (!order?.id) {
      setNotes([])
      return
    }
    let cancelled = false
    setLoading(true)
    getNotesList({ parent_id: order.id, note_type: NoteType.SeedOrderNote }, {}, 1, 500)
      .then((res) => {
        if (!cancelled) setNotes(res.data ?? [])
      })
      .catch(() => {
        if (!cancelled) void message.error(t('notes:errors.fetchFailed', 'Failed to load notes.'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [order?.id, t])

  const handleCancel = () => {
    setNoteText('')
    onCancelAddNote()
  }

  const handleSave = async () => {
    if (!order?.id || !noteText.trim()) {
      onSaveAddNote()
      return
    }
    const createdBy = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()
    setSaving(true)
    try {
      const created = await addNote({
        parent_id: order.id,
        note_type: NoteType.SeedOrderNote,
        note_text: noteText.trim(),
        created_by: createdBy || undefined,
      })
      setNotes((prev) => [created, ...prev])
      setNoteText('')
      onSaveAddNote()
    } catch {
      void message.error(t('notes:errors.saveFailed', 'Failed to save note.'))
    } finally {
      setSaving(false)
    }
  }

  const handleEditNote = (note: Note) => {
    setEditingNoteId(note.id)
    setEditText(note.note_text ?? '')
  }

  const handleCancelEdit = () => {
    setEditingNoteId(null)
    setEditText('')
  }

  const handleSaveEdit = async () => {
    if (!editingNoteId || !editText.trim()) {
      handleCancelEdit()
      return
    }
    setEditSaving(true)
    try {
      const updated = await updateNote(editingNoteId, { note_text: editText.trim() })
      setNotes((prev) => prev.map((n) => (n.id === editingNoteId ? updated : n)))
      setEditingNoteId(null)
      setEditText('')
      void message.success(t('notes:actions.updateSuccess', 'Note updated.'))
    } catch {
      void message.error(t('notes:actions.updateFailed', 'Failed to update note.'))
    } finally {
      setEditSaving(false)
    }
  }

  const handleDeleteNote = (noteId: string) => {
    setNotes((prev) => prev.filter((n) => n.id !== noteId))
  }

  return (
    <Flex vertical gap="small" style={seedOrderNotesRootStyle}>
      {isAddingNote && !editingNoteId && (
        <>
          <Input.TextArea
            value={noteText}
            onChange={(event) => setNoteText(event.target.value)}
            placeholder="Add Note..."
            autoSize={{ minRows: 3, maxRows: 6 }}
          />
          <Flex justify="flex-end" gap={8}>
            <Button onClick={handleCancel} disabled={saving}>
              {t('notes:form.cancel', 'Cancel')}
            </Button>
            <Button type="primary" onClick={handleSave} loading={saving} disabled={!noteText.trim()}>
              {t('notes:form.save', 'Save')}
            </Button>
          </Flex>
        </>
      )}
      {loading ? (
        <Flex justify="center" style={seedOrderNotesLoadingStyle}>
          <Spin />
        </Flex>
      ) : notes.length === 0 ? (
        <div style={seedOrderNotesEmptyStyle}>{t('notes:list.empty', 'No notes yet.')}</div>
      ) : (
        <div style={seedOrderNotesTimelineStyle}>
          <div style={seedOrderNotesRailStyle} />
          <Flex vertical gap={16}>
            {notes.map((note) => (
              <div key={note.id} style={seedOrderNotesItemStyle}>
                {editingNoteId === note.id ? (
                  <>
                    <Input.TextArea
                      value={editText}
                      onChange={(event) => setEditText(event.target.value)}
                      placeholder="Edit Note..."
                      autoSize={{ minRows: 3, maxRows: 6 }}
                    />
                    <Flex justify="flex-end" gap={8} style={seedOrderNotesEditActionsStyle}>
                      <Button onClick={handleCancelEdit} disabled={editSaving}>
                        {t('notes:form.cancel', 'Cancel')}
                      </Button>
                      <Button type="primary" onClick={handleSaveEdit} loading={editSaving} disabled={!editText.trim()}>
                        {t('notes:form.save', 'Save')}
                      </Button>
                    </Flex>
                  </>
                ) : (
                  <>
                    <div style={seedOrderNotesMarkerStyle} />
                    {!hideMoreOptions && isCurrentUserNote(note.created_by) ? (
                      <div style={seedOrderNotesMoreActionsWrapStyle}>
                        <STNoteDropdownActions note={note} onEdit={handleEditNote} onDeleteSuccess={handleDeleteNote} />
                      </div>
                    ) : null}
                    <div style={seedOrderNotesTextStyle}>{note.note_text}</div>
                    <div style={seedOrderNotesMetaStyle}>{formatNoteMeta(note.created_at, note.created_by)}</div>
                  </>
                )}
              </div>
            ))}
          </Flex>
        </div>
      )}
    </Flex>
  )
}

export default SeedOrderNotes
